import React from 'react';
import '@/assets/scss/application.scss';
import '@/assets/scss/soft-ui-custom.scss';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import queryClient from '@/lib/queryClient';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { LoadingProvider } from '@/providers/LoadingProvider';
import { AppRouter } from '@/routes';
import { NavStateProvider } from '@/providers/NavStateProvider';
import { WorkspaceProvider } from '@/providers/WorkspaceProvider';
import { TeamProvider } from '@/providers/TeamProvider';
import { TeamMemberProvider } from '@/providers/TeamMemberProvider';
import { SearchConditionProvider } from '@/providers/SearchConditionProvider';
import { BackofficeSearchConditionProvider } from '@/providers/BackofficeSearchConditionProvider';
import ja from 'javascript-time-ago/locale/ja.json';
import TimeAgo from 'javascript-time-ago';
import { ApprovalSearchConditionProvider } from '@/providers/ApprovalSearchConditionProvider';
import { DashboardSearchConditionProvider } from '@/providers/DashboardSearchConditionProvider';
import * as Sentry from '@sentry/react';
import { PrimeReactProvider, PrimeReactContext } from 'primereact/api';
import 'primereact/resources/themes/lara-light-cyan/theme.css';

function App() {
  TimeAgo.addDefaultLocale(ja);

  if (process.env.NODE_ENV != 'development') {
    Sentry.init({
      dsn: 'https://bb659f84121a47fc711cde4f6f2499b4@o4507490424782848.ingest.us.sentry.io/4507490429435904',
      integrations: [
        Sentry.browserTracingIntegration(),
        // Sentry.replayIntegration()
      ],
      // Performance Monitoring
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [/https:\/\/stg-console\.biz-node\.app/, /https:\/\/console\.biz-node\.app/],
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
  }

  return (
    <QueryClientProvider client={queryClient}>
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
      <ToastContainer />
      <PrimeReactProvider>
        <BrowserRouter>
          <LoadingProvider>
            <WorkspaceProvider>
              <NavStateProvider>
                <TeamProvider>
                  <TeamMemberProvider>
                    <DashboardSearchConditionProvider>
                      <SearchConditionProvider>
                        <BackofficeSearchConditionProvider>
                          <ApprovalSearchConditionProvider>
                            <AppRouter />
                          </ApprovalSearchConditionProvider>
                        </BackofficeSearchConditionProvider>
                      </SearchConditionProvider>
                    </DashboardSearchConditionProvider>
                  </TeamMemberProvider>
                </TeamProvider>
              </NavStateProvider>
            </WorkspaceProvider>
          </LoadingProvider>
        </BrowserRouter>
      </PrimeReactProvider>
    </QueryClientProvider>
  );
}

export default App;

//npx @sentry/wizard@latest -i sourcemaps
//
// Sentry.init({
//   dsn: "https://bb659f84121a47fc711cde4f6f2499b4@o4507490424782848.ingest.us.sentry.io/4507490429435904",
//   integrations: [
//     Sentry.browserTracingIntegration(),
//     Sentry.replayIntegration(),
//   ],
//   // Performance Monitoring
//   tracesSampleRate: 1.0, //  Capture 100% of the transactions
//   // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
//   tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
//   // Session Replay
//   replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
//   replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
// });
